import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { fetchScenarioSuccess } from '../../store/slices/scenarioSlice';
import { useQuery, useMutation, useSubscription } from '@apollo/client';
import { GET_SCENARIO, GET_SESSION, GET_SERVER_TIME } from '../../graphql/queries';
import { UPDATE_SESSION, END_SESSION, GENERATE_CONVERSATION, UPDATE_CONVERSATION } from '../../graphql/mutations';
import { ON_CONVERSATION_UPDATED } from '../../graphql/subscriptions';
import { Conversation, Session, Scenario } from '../../types/models';
import { parseAiParams } from '../../utils/formatUtils';
import { isIOS } from '../../utils/deviceUtils';
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Alert,
  SelectChangeEvent,
  useTheme,
  Container,
  useMediaQuery
} from '@mui/material';
import {
  SessionEndConfirmDialog,
  MicSelectDialog,
  RoleplayVisualArea,
  RoleplayTimerBlock,
  RoleplayControlButtons,
  RoleplayMobileControlBar,
  RoleplayAiSubtitle,
  RoleplayGuideCard,
  RoleplayStatusOverlay,
} from './components';
import type { ErrorState, RoleplayStatus } from './types';
import { RootState } from '../../store';
import { configService } from '../../services/configService';
import { uploadService } from '../../services/uploadService';


import { useRoleplayAvatar } from './hooks/useRoleplayAvatar';
import { useRoleplayAudio } from './hooks/useRoleplayAudio';
import { useRoleplaySession } from './hooks/useRoleplaySession';
import { useRoleplayRecording } from './hooks/useRoleplayRecording';

const RoleplayPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { currentUser } = useSelector((state: RootState) => state.user);
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));

  // 状態管理
  const [sessionId, setSessionId] = useState<string>(id || '');
  const [scenarioId, setScenarioId] = useState<string>('');
  const [currentConversationId, setCurrentConversationId] = useState<string>('');
  const [scenario, setScenario] = useState<Scenario>({} as Scenario);
  const [messages, setMessages] = useState<Conversation[]>([]);
  const [isRecording, setIsRecording] = useState(false);
  const [showGuide, setShowGuide] = useState(true);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);
  const [characterSettings, setCharacterSettings] = useState('気さくで話に乗りやすい');
  const [enableSubtitles, setEnableSubtitles] = useState(true);
  const [guideText, setGuideText] = useState<string>('');
  const [appConfig, setAppConfig] = useState<any>(null);
  const [conversationCounter, setConversationCounter] = useState(0);
  const [aiScore, setAiScore] = useState<number>(0);
  const [hasFirstConversationCompleted, setHasFirstConversationCompleted] = useState<boolean>(false);
  const [isSubtitleExpanded, setIsSubtitleExpanded] = useState<boolean>(false);
  const [recordingStartTime, setRecordingStartTime] = useState<number>(0);
  const [recordingDuration, setRecordingDuration] = useState<number>(0);
  const [audioContextInitialized, setAudioContextInitialized] = useState<boolean>(false);

  const [status, setStatus] = useState<RoleplayStatus>('idle');
  const [error, setError] = useState<ErrorState>(null);

  const {
    audioRecorder,
    isAudioInitialized,
    audioDevices,
    selectedAudioDevice,
    setSelectedAudioDevice,
    showMicDialog,
    setShowMicDialog,
    handleMicChange,
    openMicDialog,
    initializeWithSelectedMic,
  } = useRoleplayAudio({
    onError: (err) => {
      setError(err);
      if (err) setStatus('idle');
    },
  });

  const avatarImage = useRoleplayAvatar({
    targetGender: scenario?.targetGender,
    targetAge: scenario?.targetAge,
    targetLanguage: scenario?.targetLanguage,
    aiScore,
    status,
    isRecording,
  });

  // GraphQLクエリでシナリオデータとセッションデータを取得
  const { loading: scenarioLoading, error: scenarioError, data, refetch: refetchScenario } = useQuery(GET_SCENARIO, {
    variables: { scenarioId },
    skip: !scenarioId,
    onCompleted: (data) => {
      if (data?.getScenario) {
        setScenario(data.getScenario);
        setScenarioId(data.getScenario.scenarioId);
        // ReduxストアにcurrentScenarioを設定
        dispatch(fetchScenarioSuccess(data.getScenario));
      }
    }
  });

  // セッション情報を取得
  const { loading: sessionLoading, error: sessionError, data: sessionData } = useQuery(GET_SESSION, {
    variables: { sessionId },
    skip: !sessionId,
    onCompleted: (data) => {
      if (data?.getSession) {
        const session: Session = data.getSession;
        // セッションのステータスに応じて処理を分岐
        if (session.status === 'COMPLETED' || session.status === 'EVALUATED' || session.status === 'EVALUATION_FAILED' || session.status === 'EVALUATION_SKIPPED') {
          // 評価ページに遷移
          navigate(`/evaluation/${session.sessionId}`);
        } else if (session.status === 'CREATED' || session.status === 'IN_PROGRESS') {
          // セッションを回復（シナリオIDを設定）
          if (session.scenarioId) {
            // シナリオデータを取得するためにクエリを再実行
            refetchScenario({ scenarioId: session.scenarioId });
            
            if (session.sessionId) {
              setSessionId(session.sessionId);
            }
            if (session.characterSettings) {
              setCharacterSettings(session.characterSettings);
            }
            if (session.aiGuide !== undefined) {
              setShowGuide(session.aiGuide);
            }
            if (session.aiSubtitleDisplay !== undefined) {
              setEnableSubtitles(session.aiSubtitleDisplay);
            }
          }
          
          // 会話カウンターを復帰
          if (session.conversations && session.conversations.length > 0) {
            // 最大のカウンター値を見つける
            const maxCounter = Math.max(...session.conversations.map((conv: Conversation) => conv.counter));
            setConversationCounter(maxCounter);
          }
        }
      }
    }
  });

  // サーバー時間を取得（経過時間はセッション開始時刻との差）
  const { data: serverTimeData } = useQuery(GET_SERVER_TIME, {
    pollInterval: 10000,
  });

  const {
    sessionTime,
    formatTime,
    getTimeColor,
    endSession,
  } = useRoleplaySession({
    sessionId,
    sessionStartTime: sessionData?.getSession?.startTime,
    serverTimeMs: serverTimeData?.getServerTime,
    navigate,
    onError: (err) => {
      setError({
        type: 'general',
        message: err.message || 'セッションの終了に失敗しました。しばらくしてから再度お試しください。',
      });
    },
  });

  // GraphQLミューテーション
  const [updateSessionMutation] = useMutation(UPDATE_SESSION);
  const [generateConversationMutation] = useMutation(GENERATE_CONVERSATION);
  const [updateConversationMutation] = useMutation(UPDATE_CONVERSATION);
  
  // GraphQLサブスクリプション
  const { data: conversationData, loading: conversationLoading, error: conversationSubscriptionError } = useSubscription(
    ON_CONVERSATION_UPDATED,
    {
      variables: { sessionId },
      skip: !sessionId,
      // onSubscriptionData({ subscriptionData }) {
      //   console.log('conversationData arrival.');
      // },
      onError(error) {
        console.error('Conversation subscription error:', error);
        setError({ type: 'general', message: `会話更新Subscriptionでエラーが発生しました: ${error.message}` });
      },
    }
  );
  
  // アプリケーション設定の読み込み
  useEffect(() => {
    const loadAppConfig = async () => {
      try {
        const config = await configService.loadConfig();
        setAppConfig(config);
      } catch (error) {
        console.error('Config load failed:', error);
        const errorMessage = error instanceof Error ? error.message : '不明なエラー';
        setError({ type: 'general', message: `アプリケーション設定の読み込みに失敗しました: ${errorMessage}` });
      }
    };
  
    loadAppConfig();
  }, []); // 一度だけ実行
  
  // タイマー用のRef
  const audioRef = useRef<HTMLAudioElement>(null);
  const recordingTimerRef = useRef<NodeJS.Timeout | null>(null);
  // サブスクリプションで既に messages に追加済みの conversationId（後着の AI ガイド時にガイドのみ差し替えるため）
  const addedConversationIdsRef = useRef<Set<string>>(new Set());

  // セッション変更時に追加済みIDをクリア
  useEffect(() => {
    addedConversationIdsRef.current = new Set();
  }, [sessionId]);

  // 会話サブスクリプションの処理
  useEffect(() => {
    // コンポーネントがマウントされているかを追跡するためのフラグ
    let isMounted = true;
    
    if (conversationData?.onConversationUpdated) {
      const conversation = conversationData.onConversationUpdated;

      // debug用
      // console.log('Conversation updated:', conversation);
      
      // コンポーネントがまだマウントされているか確認
      if (isMounted) {
        // 新しい会話が来たら字幕の展開状態をリセット
        setIsSubtitleExpanded(false);
        
        // statusがCOMPLETEDまたはFAILUREの場合のみ処理を開始
        if (conversation.status === 'COMPLETED' || conversation.status === 'FAILURE') {
          const cid = conversation.conversationId;
          const alreadyAdded = cid ? addedConversationIdsRef.current.has(cid) : false;

          // 既に追加済みで、今回の更新が aiGuide の後着の場合はガイド部分のみ差し替え（字幕は上書きしない）
          if (alreadyAdded && conversation.aiGuide) {
            setGuideText(conversation.aiGuide);
            // 同一会話の aiContent が後から届いた場合は最後のメッセージを更新（字幕を表示するため）
            if (conversation.aiContent && cid) {
              setMessages(prev => {
                const last = prev[prev.length - 1];
                if (last?.conversationId === cid) {
                  return [...prev.slice(0, -1), { ...last, aiContent: conversation.aiContent ?? last.aiContent }];
                }
                return prev;
              });
            }
            return;
          }
          if (alreadyAdded) return;

          // aiContent が無く aiGuide のみの更新は追加しない（追加すると最後のメッセージが空になり字幕が消える）
          if (conversation.aiGuide && !conversation.aiContent) {
            if (cid) addedConversationIdsRef.current.add(cid);
            setGuideText(conversation.aiGuide);
            return;
          }

          if (cid) addedConversationIdsRef.current.add(cid);
          // 会話データを追加（userContentとaiContentの両方を含む）
          setMessages(prev => [...prev, conversation]);

          if(conversation.status === 'COMPLETED') {
            const conversationId = currentConversationId;
          
            // AIガイドの更新
            if (conversation.aiGuide) {
              setGuideText(conversation.aiGuide);
            }

            // AI評価の更新
            if (conversation.aiParams) {
              const aiParams = parseAiParams(conversation.aiParams);
              if (aiParams && aiParams.level && aiParams.level.score) {
                setAiScore(aiParams.level.score);
              }
            }
            
            // AI応答の処理
            if (conversation.aiContent) {
              setStatus('aiSpeaking');
              
              // 音声再生
              if (conversation.aiAudioPath && conversationId) {
                console.log('Playing audio for conversation:', conversationId);
                playAudio(conversationId);
              } else {
                // 音声URLがない場合または会話IDがない場合は、一定時間後に話し終わったとみなす
                console.warn('No audio path or conversation ID:', {
                  aiAudioPath: conversation.aiAudioPath,
                  conversationId: conversationId
                });
                
                // タイムアウトを設定する前にコンポーネントがマウントされているか確認
                const timer = setTimeout(() => {
                  if (isMounted) {
                    setStatus('idle');
                  }
                }, 2000);
                
                // クリーンアップ関数でタイマーをクリア
                return () => {
                  clearTimeout(timer);
                };
              }
            }
          }
          
          // 処理待ち状態を解除
          setStatus('idle');
        }
      }
    }
    
    return () => {
      isMounted = false;
    };
  }, [conversationData]);
  
  // 音声再生処理（iOS対応版）
  const playAudio = async (conversationId: string) => {
    try {
      if (!sessionId || !conversationId) {
        console.error('Invalid sessionId or conversationId:', { sessionId, conversationId });
        setStatus('idle');
        return;
      }

      // 署名付きURLを取得
      const baseUrl = appConfig?.apiUrl?.endsWith('/') ? appConfig.apiUrl.slice(0, -1) : appConfig?.apiUrl;
      if (!baseUrl) {
        console.error('API URL is not defined');
        setStatus('idle');
        return;
      }

      const idToken = localStorage.getItem('idToken') || sessionStorage.getItem('idToken');
      if (!idToken) {
        console.error('ID token is not available');
        setStatus('idle');
        return;
      }

      const requestBody = {
        sessionId: sessionId,
        conversationId: conversationId,
        audioType: 'AI'
      };
      
      const response = await fetch(`${baseUrl}/get-audio-url`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
        },
        body: JSON.stringify(requestBody),
      });

      const data = await response.json();
      
      if (data.success && data.presignedUrl) {
        if (audioRef.current) {
          // iOS Chrome用のAudioContext初期化
          if (isIOS() && !audioContextInitialized) {
            try {
              const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
              if (AudioContext) {
                const audioContext = new AudioContext();
                if (audioContext.state === 'suspended') {
                  await audioContext.resume();
                }
                setAudioContextInitialized(true);
                console.log('iOS Chrome: AudioContext initialized');
              }
            } catch (contextError) {
              console.warn('Failed to initialize AudioContext:', contextError);
            }
          }

          audioRef.current.src = data.presignedUrl;
          audioRef.current.volume = 1.0;
          
          // iOS Chrome向けの詳細設定
          if (isIOS()) {
            audioRef.current.preload = 'auto';
            audioRef.current.muted = false;
            audioRef.current.autoplay = false; // 明示的に無効化
            // iOS特有の属性設定
            audioRef.current.setAttribute('playsinline', 'true');
            audioRef.current.setAttribute('webkit-playsinline', 'true');
            audioRef.current.setAttribute('controls', 'false');
          }
          
          audioRef.current.onended = () => {
            setStatus('idle');
            if (!hasFirstConversationCompleted) {
              setHasFirstConversationCompleted(true);
            }
          };
          
          audioRef.current.onerror = (e) => {
            console.error('Audio playback error:', e);
            setStatus('idle');
          };
          
          // iOS Chrome用の強化された再生処理
          const playWithRetry = async (retryCount = 0) => {
            try {
              if (!audioRef.current) {
                console.error('Audio element is null');
                setStatus('idle');
                return;
              }

              // iOS Chromeではファイル読み込み完了を確実に待つ
              if (isIOS() && audioRef.current.readyState < 3) {
                console.log(`iOS Chrome: Waiting for audio load completion. ReadyState: ${audioRef.current.readyState}`);
                
                // 読み込み完了を待つPromise
                await new Promise<void>((resolve, reject) => {
                  if (!audioRef.current) {
                    reject(new Error('Audio element became null'));
                    return;
                  }
                  
                  if (audioRef.current.readyState >= 3) {
                    resolve();
                    return;
                  }
                  
                  const timeout = setTimeout(() => {
                    reject(new Error('Audio load timeout'));
                  }, 5000);
                  
                  audioRef.current.onloadeddata = () => {
                    clearTimeout(timeout);
                    resolve();
                  };
                  
                  audioRef.current.onerror = () => {
                    clearTimeout(timeout);
                    reject(new Error('Audio load error'));
                  };
                  
                  audioRef.current.load();
                });
              }

              console.log(`iOS Chrome: Attempting audio play (attempt ${retryCount + 1})`);
              await audioRef.current.play();
              console.log('Audio playback started successfully');
              
            } catch (error) {
              console.error(`Audio play attempt ${retryCount + 1} failed:`, error);
              
              // iOS Chromeでの詳細なエラーハンドリング
              if (error instanceof DOMException) {
                if (error.name === 'NotAllowedError') {
                  if (retryCount < 3) {
                    console.warn(`iOS Chrome: NotAllowedError. Retrying in ${(retryCount + 1) * 200}ms...`);
                    setTimeout(() => playWithRetry(retryCount + 1), (retryCount + 1) * 200);
                    return;
                  } else {
                    console.error('iOS Chrome: Max retries reached for NotAllowedError');
                  }
                } else if (error.name === 'AbortError') {
                  if (retryCount < 3) {
                    console.warn(`iOS Chrome: AbortError. Retrying in ${(retryCount + 1) * 300}ms...`);
                    setTimeout(() => playWithRetry(retryCount + 1), (retryCount + 1) * 300);
                    return;
                  }
                } else if (error.name === 'NotSupportedError') {
                  console.error('iOS Chrome: Audio format not supported');
                } else if (error.name === 'NetworkError') {
                  if (retryCount < 2) {
                    console.warn('iOS Chrome: NetworkError. Retrying...');
                    setTimeout(() => playWithRetry(retryCount + 1), 1000);
                    return;
                  }
                }
              }
              
              console.error('Final audio play failure:', error);
              setStatus('idle');
            }
          };
          
          // iOS Chromeでは十分な遅延を設ける
          if (isIOS()) {
            // AudioContextの初期化完了とファイル読み込み完了を待ってから再生
            setTimeout(() => playWithRetry(), 200);
          } else {
            await playWithRetry();
          }
        }
      } else {
        console.error('Failed to get audio URL:', data.message);
        setStatus('idle');
      }
    } catch (error) {
      console.error('Error getting audio URL:', error);
      setStatus('idle');
    }
  };
  

  // セッションを開始する（1回のみ）
  useEffect(() => {
    // ページ読み込み時に最上部にスクロール
    window.scrollTo(0, 0);
    
    // 設定の読み込み
    const initSettings = async () => {
      try {
        // タイマーの開始処理は削除済み
      } catch (error) {
        console.error('Session initialization failed:', error);
        const errorMessage = error instanceof Error ? error.message : '不明なエラー';
        setError({ type: 'general', message: `セッションの初期化に失敗しました: ${errorMessage}` });
      }
    };
    
    initSettings();
    
    // クリーンアップ
    return () => {
      localStorage.removeItem('roleplaySettings');
      
      // マイクアクセスをOFFにする
      const recorder = audioRecorder.current;
      if (recorder?.mediaRecorder) {
        if (recorder.isRecording()) {
          recorder.mediaRecorder.stop();
        }
        if (recorder.stream) {
          recorder.stream.getTracks().forEach((track) => track.stop());
        }
      }
      
      // 録音状態をリセット
      setIsRecording(false);
      
      // 録音タイマーのクリーンアップ
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
    };
  }, []);
  
  // 録音の開始/停止
  const toggleRecording = async () => {
    // 初期化チェック（WebRTCとMediaRecorderの両方に対応）
    const isRecorderInitialized = audioRecorder.current.useWebRTC 
      ? audioRecorder.current.webrtcRecorder !== null
      : audioRecorder.current.mediaRecorder !== null;
      
    if (!isAudioInitialized || !isRecorderInitialized) {
      const initErrorMsg = '録音機能が初期化されていません。ページを再読み込みするか、マイクアクセス許可を確認してください。';
      setError({ type: 'general', message: initErrorMsg });
      setStatus('idle');
      return;
    }

    if (status === 'recording') {
      // 録音停止処理
      console.log('Stopping recording...');
      setIsRecording(false);
      setStatus('processing');
      
      // 録音時間タイマーの停止
      if (recordingTimerRef.current) {
        clearInterval(recordingTimerRef.current);
        recordingTimerRef.current = null;
      }
      
      // 録音時間のチェック（最小録音時間）
      const finalDuration = recordingDuration;
      const minDuration = 1;
      
      console.log(`Recording duration: ${finalDuration}s (minimum: ${minDuration}s for ${isIOS() ? 'iOS' : 'other'})`);
      
      if (finalDuration < minDuration) {
        console.warn(`Recording too short: ${finalDuration}s < ${minDuration}s`);
        const message = `録音時間が短すぎます（${finalDuration}秒）。${minDuration}秒以上話してください。`;
        
        setError({ type: 'general', message });
        setStatus('idle');
        return;
      }
      
      try {
        // 録音データの取得
        const audioBlob = await audioRecorder.current.stop();
        console.log('Recording stopped. Blob received:', {
          size: audioBlob.size,
          type: audioBlob.type,
          chunks: audioRecorder.current.audioChunks.length
        });

        if (audioBlob.size === 0) {
          console.error('Recorded audio blob is empty. Upload cancelled.');
          
          const errorMessage = '録音データが空のようです。十分な時間話したか、マイクが機能しているか確認してください。';
          
          setError({ type: 'general', message: errorMessage });
          setStatus('idle');
          
          return;
        }
        
        // カウンターをインクリメント
        const nextCounter = conversationCounter + 1;
        setConversationCounter(nextCounter);
        
        // 会話生成ミューテーションを実行
        let conversationId = '';
        try {
          const generateResponse = await generateConversationMutation({
            variables: {
              input: {
                sessionId,
                counter: nextCounter
              }
            }
          });
          conversationId = generateResponse.data?.generateConversation?.conversationId || '';
          // console.log('Generated conversation with ID:', conversationId);

          // session.statusをIN_PROGRESSに変更
          const sessionUpdateResponse = updateSessionMutation({
            variables: {
              input: {
                sessionId: sessionId,
                status: 'IN_PROGRESS'
              }
            }
          });

        } catch (mutationError) {
          console.error('Failed to generate conversation:', mutationError);
          setError({ type: 'general', message: '会話の生成に失敗しました' });
          setStatus('idle');
          return;
        }
        
        if (!conversationId) {
          setError({ type: 'general', message: '会話IDの取得に失敗しました' });
          setStatus('idle');
          return;
        }

        // 最新のConversationidを設定
        setCurrentConversationId(conversationId);
        
        // シナリオ情報を取得
        const scenarioId = scenario?.scenarioId || '';
        
        // ファイル名と拡張子を決定 (Blobのtypeを優先)
        const blobType = audioBlob.type || audioRecorder.current.mimeType;
        let fileExtension = 'bin'; // 不明な場合のデフォルト
        if (blobType.includes('webm')) fileExtension = 'webm';
        else if (blobType.includes('ogg')) fileExtension = 'ogg';
        else if (blobType.includes('mp4')) fileExtension = 'mp4'; // mp4を使用
        else if (blobType.includes('aac')) fileExtension = 'aac';
        else if (blobType) { // 他のタイプがあれば、それを拡張子に（例: audio/wav -> wav）
          const parts = blobType.split('/');
          if (parts.length > 1) fileExtension = parts[1].split(';')[0]; // codecs情報などを除去
        }
        // console.log(`Determined file extension: .${fileExtension} based on Blob type: ${blobType || '(empty)'}, Recorder MIME: ${audioRecorder.current.mimeType}`);
        const fileName = `in.${fileExtension}`;

        // 音声ファイル名を構築
        const companyId = currentUser?.companyId || ''; // '' のチェックを追加
        const currentScenarioId = scenario?.scenarioId || ''; // '' のチェックを追加
        if (!companyId || !currentScenarioId) {
             console.error('Cannot upload: companyId or scenarioId is empty.');
             setError({ type: 'general', message: 'アップロードに必要な情報(会社IDまたはシナリオID)が取得できませんでした。' });
             setStatus('idle');
             return;
        }
        const paddedCounter = nextCounter.toString().padStart(3, '0'); // カウンターはここで計算
        const s3Key = `audios/${companyId}/${currentScenarioId}/${sessionId}/${paddedCounter}/${fileName}`;
        
        if (appConfig) {
          try {
            // console.log(`Uploading audio: key=${s3Key}, name=${fileName}, size=${audioBlob.size}, type=${blobType}`);

            // 署名付きURLを取得
            const uploadUrl = await uploadService.getUploadUrl(
              companyId,
              s3Key,
              fileName,
              blobType,
              scenarioId
            );
            
            // 音声ファイルの整合性を確認
            if (audioBlob.size === 0) {
              console.error('Audio blob is empty');
              setError({ type: 'general', message: '録音データが空です。もう一度お試しください。' });
              setStatus('idle');
              return;
            }
            
            // 音声をアップロード（BlobをFileに変換）
            const audioFile = new File([audioBlob], fileName, { type: blobType });
            await uploadService.uploadFileWithPresignedUrl(uploadUrl, audioFile);
            // console.log('Audio uploaded successfully');
            
            // 会話を更新（CREATED -> UPLOADED）
            try {
              const updateResponse = await updateConversationMutation({
                variables: {
                  input: {
                    sessionId,
                    conversationId,
                    status: 'UPLOADED',
                    userAudioPath: s3Key,
                  }
                }
              });
              
              // console.log('Updated conversation with ID:', conversationId);
            } catch (mutationError) {
              console.error('Failed to update conversation:', mutationError);
              setError({ type: 'general', message: '会話の更新に失敗しました' });
              setStatus('idle');
              return;
            }

          } catch (uploadError) {
            console.error('Error uploading audio:', uploadError);
            setError({ type: 'general', message: '音声のアップロードに失敗しました' });
          }
        } else {
          // configがロードされていない場合
          console.warn('Config not loaded.');
        }
      } catch (error) {
        console.error('Error processing audio:', error);
        const errorMessage = error instanceof Error ? error.message : '不明なエラー';
        setError({ type: 'general', message: `音声処理中にエラーが発生しました: ${errorMessage}` });
      } finally {
        setStatus('idle');
        // サーバー側の処理が完了するまで待機状態にする
        setStatus('waiting');
      }
    } else {
        // 録音開始処理
        if (status === 'processing' || status === 'waiting') {
          console.warn("Cannot start recording now. Current status:", status);
          return;
        }
        
        console.log('Starting recording...');
        
        try {
          if (audioRecorder.current.useWebRTC) {
            // WebRTC録音の場合の状態チェック
            if (!audioRecorder.current.webrtcRecorder) {
              throw new Error('WebRTC Recorder is not initialized');
            }
            
            if (audioRecorder.current.webrtcRecorder.isRecording) {
              console.warn('WebRTC Recorder is already recording');
              return;
            }
          } else {
            // MediaRecorderの状態を事前にチェック
            if (!audioRecorder.current.mediaRecorder) {
              throw new Error('MediaRecorder is not initialized');
            }
            
            const currentState = audioRecorder.current.mediaRecorder.state;
            console.log('MediaRecorder state before start:', currentState);
            
            // 不正な状態の場合は再初期化
            if (currentState !== 'inactive') {
              console.warn('MediaRecorder not in inactive state, attempting cleanup and reinit...');
              audioRecorder.current.cleanup();

              // 再初期化を試行
              await initializeWithSelectedMic(selectedAudioDevice || '');
              
              // 再初期化後の状態確認
              if (!audioRecorder.current.mediaRecorder || audioRecorder.current.mediaRecorder.state !== 'inactive') {
                throw new Error('Failed to reinitialize MediaRecorder');
              }
            }
          }
          
          // エラー状態をクリアしてから開始
          setError(null);
          setStatus('recording');
        
        // 録音開始
        audioRecorder.current.start();
        setIsRecording(true);
        
        // 録音時間の監視開始
        const startTime = Date.now();
        setRecordingStartTime(startTime);
        setRecordingDuration(0);
        
        recordingTimerRef.current = setInterval(() => {
          const elapsed = Math.floor((Date.now() - startTime) / 1000);
          setRecordingDuration(elapsed);
        }, 100);
        
      } catch (error) {
        console.error('Failed to start recording:', error);
        const errorMessage = error instanceof Error ? error.message : '不明なエラー';
        setError({ type: 'audio', message: `録音開始失敗: ${errorMessage}` });
        setStatus('idle');
        setIsRecording(false);
        
        // タイマーをクリーンアップ
        if (recordingTimerRef.current) {
          clearInterval(recordingTimerRef.current);
          recordingTimerRef.current = null;
        }
        
      }
    }
  };

  const { speakButtonRef } = useRoleplayRecording({
    toggleRecording,
    status,
    openConfirmDialog,
    showMicDialog,
  });

  const handleEndSession = () => {
    setOpenConfirmDialog(true);
  };

  const confirmEndSession = async () => {
    setOpenConfirmDialog(false);
    try {
      await endSession();
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : '不明なエラー';
      setError({ type: 'general', message: `セッションの終了に失敗しました: ${errorMessage}` });
    }
  };

  // ローディング表示
  if (scenarioLoading || sessionLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          シナリオを読み込んでいます...
        </Typography>
      </Box>
    );
  }

  // エラー表示
  if (sessionError) {
    return (
      <Box sx={{ mt: 4 }}>
        <Alert severity="error">
          セッションの読み込みに失敗しました。再度お試しください。
        </Alert>
        <Button
          variant="contained"
          onClick={() => navigate('/scenarios')}
          sx={{ mt: 2 }}
        >
          シナリオ一覧に戻る
        </Button>
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{
        mt: '6px', height: '100vh', display: 'flex', flexDirection: 'column',
        padding: '0px'
      }}>
      {/* エラー表示 */}
      {error && (
        <Alert 
          severity="error" 
          onClose={() => setError(null)}
          sx={{ mb: 2 }}
        >
          {error.message}
        </Alert>
      )}
      
      {/* 新しいレイアウト - roleplay-root */}
      <Box sx={{ 
        width: '100%', 
        maxWidth: '1200px', 
        margin: '0 auto',
        display: 'flex',
        flexDirection: 'column',
        gap: '16px',
        alignItems: 'center',
        flexGrow: 1
      }}>

        {/* ロープレビジュアルエリア */}
        <Box sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          position: 'relative',
          gap: 0,
        }}>
          <Box sx={{
            width: '100%',
            aspectRatio: '16/7',
            position: 'relative',
            overflow: 'visible',
          }}>
            <RoleplayVisualArea
              avatarImage={avatarImage}
              status={status}
              isSmallScreen={isSmallScreen}
            />
            {!isSmallScreen && (
              <>
                <RoleplayTimerBlock
                  sessionTime={sessionTime}
                  estimatedTime={scenario.estimatedTime || 30}
                  formatTime={formatTime}
                  getTimeColor={getTimeColor}
                />
                <RoleplayControlButtons
                  status={status}
                  hasFirstConversationCompleted={hasFirstConversationCompleted}
                  toggleRecording={toggleRecording}
                  handleEndSession={handleEndSession}
                  navigate={navigate}
                  openMicDialog={openMicDialog}
                  speakButtonRef={speakButtonRef}
                />
                {/* 字幕はアバター背景の下端に揃え、AIガイドにかぶらないよう絶対配置 */}
                {messages.length > 0 && messages[messages.length - 1].aiContent && (
                  <Box sx={{ position: 'absolute', bottom: 0, left: 0, right: 0, zIndex: 4 }}>
                    <RoleplayAiSubtitle
                      lastAiContent={messages[messages.length - 1].aiContent ?? ''}
                      enableSubtitles={enableSubtitles}
                      isSmallScreen={false}
                      isSubtitleExpanded={isSubtitleExpanded}
                      onSubtitleExpandedChange={setIsSubtitleExpanded}
                    />
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>

        {isSmallScreen && (
          <RoleplayMobileControlBar
            sessionTime={sessionTime}
            estimatedTime={scenario.estimatedTime || 30}
            formatTime={formatTime}
            getTimeColor={getTimeColor}
            status={status}
            hasFirstConversationCompleted={hasFirstConversationCompleted}
            toggleRecording={toggleRecording}
            handleEndSession={handleEndSession}
            navigate={navigate}
            speakButtonRef={speakButtonRef}
          />
        )}

        {isSmallScreen && messages.length > 0 && messages[messages.length - 1].aiContent && (
          <RoleplayAiSubtitle
            lastAiContent={messages[messages.length - 1].aiContent ?? ''}
            enableSubtitles={enableSubtitles}
            isSmallScreen={true}
            isSubtitleExpanded={isSubtitleExpanded}
            onSubtitleExpandedChange={setIsSubtitleExpanded}
          />
        )}

        <RoleplayGuideCard
          showGuide={showGuide}
          guideText={guideText}
          isSmallScreen={isSmallScreen}
        />
      </Box>

      <RoleplayStatusOverlay status={status} isSmallScreen={isSmallScreen} />
      
      {/* 音声再生用の非表示オーディオ要素 */}
      <audio ref={audioRef} style={{ display: 'none' }} />
      
      <SessionEndConfirmDialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
        onConfirm={confirmEndSession}
      />

      <MicSelectDialog
        open={showMicDialog}
        onClose={() => setShowMicDialog(false)}
        devices={audioDevices}
        selectedDeviceId={selectedAudioDevice}
        onDeviceChange={handleMicChange}
      />
    </Container>
  );
};

export default RoleplayPage;
